import { logUserActivity, getUserIP } from '@/utils/activityLogger';
import { supabase } from '@/integrations/supabase/client';

/**
 * Utility for exporting data to CSV format
 */

/**
 * Format complex values for CSV export
 * @param value Value to format
 * @returns Formatted string value
 */
const formatValueForCsv = (value: any): string => {
  if (value === null || value === undefined) {
    return '';
  }
  
  if (typeof value === 'object') {
    try {
      // For price_set objects common in e-commerce data
      if (value.shop_money && value.presentment_money) {
        return `${value.shop_money.amount || 0} ${value.shop_money.currency_code || ''}`;
      }
      
      // Handle arrays
      if (Array.isArray(value)) {
        return value.map(item => typeof item === 'object' ? 'Item' : String(item)).join(', ');
      }
      
      // For other objects, convert to JSON
      return JSON.stringify(value);
    } catch (e) {
      return '[Complex Object]';
    }
  }
  
  return String(value);
};

/**
 * Convert array of objects to CSV string
 * @param data Array of objects to convert
 * @param columns Array of column names to include
 * @returns CSV formatted string
 */
const convertToCSV = (data: any[], columns: string[]): string => {
  // Create header row
  let csvContent = columns.map(c => `"${c.replace(/"/g, '""')}"`).join(',') + '\n';
  
  // Add data rows
  data.forEach(item => {
    const row = columns.map(column => {
      const value = item[column];
      let formattedValue = formatValueForCsv(value);
      
      // Sanitize for spreadsheet injection: prefix with single quote if starts with formula characters
      // Prevents CSV injection attacks where =, +, -, @, or tab at start could be interpreted as formulas
      if (typeof formattedValue === 'string' && /^[=+\-@\t]/.test(formattedValue)) {
        formattedValue = "'" + formattedValue;
      }
      
      // Handle strings that may contain commas or quotes
      if (typeof formattedValue === 'string') {
        // Escape quotes and wrap in quotes
        const escapedValue = formattedValue.replace(/"/g, '""');
        return `"${escapedValue}"`;
      }
      
      return formattedValue;
    }).join(',');
    
    csvContent += row + '\n';
  });
  
  return csvContent;
};

/**
 * Export data to CSV file and trigger download
 * @param data Data to export
 * @param columns Columns to include
 * @param filename Filename for the download
 * @param tableName Optional table name for logging
 * @param connectionId Optional connection ID for logging
 */
export const exportToCSV = async (
  data: any[], 
  columns: string[], 
  filename: string,
  tableName?: string,
  connectionId?: string
): Promise<void> => {
  const csvContent = convertToCSV(data, columns);
  
  // Create a blob with the CSV content
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  
  // Create a temporary URL to the blob
  const url = URL.createObjectURL(blob);
  
  // Create a link element
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  
  // Add to document, trigger click, then remove
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up the URL object
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 100);

  // Log the export activity
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (user?.email) {
      const userIP = await getUserIP();
      
      await logUserActivity({
        userId: user.id,
        userEmail: user.email,
        activityType: 'data_export',
        activityDetails: {
          filename,
          tableName: tableName || 'unknown',
          connectionId: connectionId || 'unknown',
          recordCount: data.length,
          columnCount: columns.length,
          columns: columns.slice(0, 10),
          timestamp: new Date().toISOString()
        },
        ipAddress: userIP,
        userAgent: navigator.userAgent
      });
    }
  } catch (error) {
    console.error('Error logging data export activity:', error);
    // Don't throw error to avoid breaking the export functionality
  }
};
